import { useCurrency } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import { Building, Car, HelpCircle, Settings } from "lucide-react";

export const RiskStrategicSection: React.FC<{ data: any }> = ({ data }) => {
  const currency = useCurrency();
  const getRiskColor = (color: string) => {
    switch (color.toLowerCase()) {
      case "green":
        return "text-green-600 bg-green-50 border-green-200";
      case "yellow":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "red":
        return "text-red-600 bg-red-50 border-red-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getCategoryIcon = (category: string) => {
    if (category.includes("Horizontal"))
      return <Settings className="w-5 h-5" />;
    if (category.includes("Vertical")) return <Settings className="w-5 h-5" />;
    if (category.includes("Vehicle")) return <Car className="w-5 h-5" />;
    if (category.includes("Facilities"))
      return <Building className="w-5 h-5" />;
    return <Building className="w-5 h-5" />;
  };

  const riskMetricsConfig = [
    {
      label: "Overall Risk Score",
      value: data?.riskStrategicImpact?.overallRiskScore?.value,
      color: "text-orange-600",

      tooltip:
        "The primary indicator of portfolio vulnerability, tracking the effectiveness of risk mitigation strategies.",
    },
    {
      label: "High-Risk Assets",
      value: data?.riskStrategicImpact?.highRiskAssets?.value,
      color: "text-red-600",

      tooltip:
        "The total number of assets that are classified as 'High Risk' based on their probability and consequence of failure.",
    },
    {
      label: "Investment Gap",
      value: `${currency} ${
        data?.riskStrategicImpact?.investmentGap?.value ?? "NA"
      }`,
      color: "text-red-600",
      tooltip:
        "A critical financial metric that quantifies the funding shortfall for necessary asset replacements.",
    },
    {
      label: "Emergency vs Planned",
      value: `${data?.riskStrategicImpact?.emergencyVsPlanned?.emergencyPercentage} : ${data?.riskStrategicImpact?.emergencyVsPlanned?.plannedPercentage}`,
      color: "text-orange-600",

      tooltip:
        "The ratio of reactive (emergency) work to proactive (planned) work. A higher ratio of planned work indicates better strategic control.",
    },
  ];

  return (
    <div className="space-y-6">
      <p className="page-title">Risk & Strategic Impact</p>

      {/* Risk Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {riskMetricsConfig.map((metric, idx) => (
          <Card key={idx}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex item-center gap-2">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {metric.label}
                      </p>
                    </div>
                    <div>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="h-4 w-4 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">{metric.tooltip}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                  <p className={`text-2xl font-bold ${metric.color}`}>
                    {metric.value ?? "NA"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Risk Assessment and Recent Assets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Assessment */}
        <Card>
          <CardHeader>
            <div className="flex item-center gap-2">
              <div>
                <CardTitle className="page-title">Risk Assessment</CardTitle>
              </div>
              <div className="mt-1">
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      A breakdown of the portfolio by risk level. Click a level
                      to filter the entire dashboard.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <CardDescription className="page-subtitle">
              Assets by risk priority level
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.riskAssessment && data.riskAssessment.length > 0 ? (
                data.riskAssessment.map((risk, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border-2 ${getRiskColor(
                      risk.color
                    )}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{risk.riskLevel}</p>
                        <p className="text-sm opacity-75">
                          {risk.percentage} % of total
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold">{risk.count}</p>
                        {risk.color === "red" && (
                          <Badge variant="destructive" className="mt-1">
                            Review Required
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-muted-foreground py-6">
                  No risk assessment data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recently Added Assets */}
        <Card>
          <CardHeader>
            <div className="flex item-center gap-2">
              <div>
                <CardTitle className="page-title">
                  Recently Added Assets
                </CardTitle>
              </div>
              <div className="mt-1">
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      The newest assets added to the portfolio. Click to view
                      the asset's detail page.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <CardDescription className="page-subtitle">
              Latest asset additions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.recentlyAddedAssets &&
              data.recentlyAddedAssets.length > 0 ? (
                data.recentlyAddedAssets.map((asset, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                  >
                    <div className="flex items-center space-x-3">
                      {getCategoryIcon(asset.assetCategory)}
                      <div>
                        <p className="font-medium">{asset.assetName}</p>
                        <p className="text-sm text-muted-foreground">
                          {asset.assetCategory}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{asset.dateAdded}</p>
                      <Badge variant="secondary" className="text-xs">
                        New
                      </Badge>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-muted-foreground py-6">
                  No recently added assets available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assets Overdue for Inspection */}
      <Card>
        <CardHeader>
          <div className="flex item-center gap-2">
            <div>
              <CardTitle className="page-title">Overdue Inspections</CardTitle>
            </div>
            <div className="mt-2">
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    This inspection is [X] days overdue. The priority is
                    [High/Medium/Low].
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
          <CardDescription className="page-subtitle">
            Assets requiring immediate inspection
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data?.overdueInspections && data.overdueInspections.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {data.overdueInspections.slice(0, 4).map((service, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div className="flex items-center space-x-3">
                    <Badge variant="secondary">{service.priority}</Badge>
                    <div>
                      <p className="font-medium text-sm">{service.assetName}</p>
                      <p className="text-xs text-muted-foreground">
                        Due: {service.dueDate}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-6">
              No overdue inspections available
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
